import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Card, CardContent, CardActions, Typography, Button, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select,
  MenuItem, FormControl, InputLabel, Tabs, Tab, InputAdornment, Paper,
  IconButton, Divider, Badge
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import EditIcon from '@mui/icons-material/Edit';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import CloseIcon from '@mui/icons-material/Close';
import ArticleIcon from '@mui/icons-material/Article';
import { DEFAULT_TEMPLATES, TEMPLATE_CATEGORIES, getTemplatesByCategory } from '../data/defaultTemplates';
import ClassificationBadge from '../components/ClassificationBadge';
import { useSnackbar } from '../contexts/SnackbarContext';
import { markdownToEditorContent } from '../utils/templateToEditorContent';

const VAULT_OPTIONS = ['procedures', 'legal', 'technical', 'financial', 'marketing', 'contracts', 'customer', 'hr', 'compliance'];
const CLASSIFICATION_OPTIONS = ['unclassified', 'internal', 'confidential', 'restricted', 'secret'];

const CATEGORY_COLORS = {
  Policy: '#1565c0', Operations: '#2e7d32', Legal: '#6a1b9a',
  Financial: '#e65100', HR: '#c62828', Compliance: '#00695c',
};

function extractMergeFields(content) {
  const matches = content.match(/\{\{([^}]+)\}\}/g) || [];
  const fields = [...new Set(matches.map(m => m.slice(2, -2).trim()))];
  return fields;
}

function applyMergeFields(content, values) {
  let result = content;
  Object.entries(values).forEach(([key, val]) => {
    result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), val || `[${key}]`);
  });
  return result;
}

function renderPreviewContent(content) {
  const lines = content.split('\n');
  return lines.map((line, idx) => {
    if (line.startsWith('# ')) return <Typography key={idx} variant="h5" fontWeight={700} sx={{ mt: 2, mb: 1 }}>{line.slice(2)}</Typography>;
    if (line.startsWith('## ')) return <Typography key={idx} variant="h6" fontWeight={600} sx={{ mt: 1.5, mb: 0.5, color: 'primary.main' }}>{line.slice(3)}</Typography>;
    if (line.startsWith('### ')) return <Typography key={idx} variant="subtitle1" fontWeight={600} sx={{ mt: 1 }}>{line.slice(4)}</Typography>;
    if (line === '---') return <Divider key={idx} sx={{ my: 2 }} />;
    if (line.startsWith('| ')) return <Typography key={idx} variant="body2" component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{line}</Typography>;
    if (line.startsWith('- ') || line.startsWith('* ')) return <Typography key={idx} variant="body2" sx={{ ml: 2, lineHeight: 1.8 }}>• {line.slice(2)}</Typography>;
    if (/^\d+\. /.test(line)) return <Typography key={idx} variant="body2" sx={{ ml: 2, lineHeight: 1.8 }}>{line}</Typography>;
    if (line.startsWith('**')) return <Typography key={idx} variant="body2" fontWeight={600} sx={{ lineHeight: 1.8 }}>{line.replace(/\*\*/g, '')}</Typography>;
    if (line === '') return <Box key={idx} sx={{ height: 8 }} />;
    return <Typography key={idx} variant="body2" sx={{ lineHeight: 1.8 }}>{line}</Typography>;
  });
}

export default function TemplateBrowser({ onNavigate }) {
  const navigate = useNavigate();
  const { showSnack } = useSnackbar();
  const [categoryTab, setCategoryTab] = useState(0);
  const [search, setSearch] = useState('');
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [useTemplate, setUseTemplate] = useState(null);
  const [useForm, setUseForm] = useState({ title: '', vault: '', classification: '', mergeFields: {} });
  const [openingEditor, setOpeningEditor] = useState(false);

  const selectedCategory = TEMPLATE_CATEGORIES[categoryTab];

  const filteredTemplates = useMemo(() => {
    let templates = getTemplatesByCategory(selectedCategory);
    if (search.trim()) {
      const q = search.toLowerCase();
      templates = templates.filter(t =>
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q) ||
        (t.tags || []).some(tag => tag.includes(q))
      );
    }
    return templates;
  }, [selectedCategory, search]);

  const handleOpenUse = (template) => {
    const mergeFields = extractMergeFields(template.content);
    const defaultMergeValues = {};
    mergeFields.forEach(f => { defaultMergeValues[f] = ''; });
    setUseTemplate(template);
    setUseForm({ title: template.name, vault: template.vault, classification: template.classification, mergeFields: defaultMergeValues });
  };

  const handleOpenInEditor = () => {
    if (!useForm.title.trim()) {
      showSnack('Please enter a document title', 'warning');
      return;
    }
    setOpeningEditor(true);
    try {
      const mergedContent = applyMergeFields(useTemplate.content, useForm.mergeFields);
      const editorContent = markdownToEditorContent(mergedContent, {
        title: useForm.title,
        subtitle: useTemplate.description || '',
      });
      navigate('/editor', {
        state: {
          editorBootstrap: {
            content: editorContent,
            docSettings: {
              vault: useForm.vault || useTemplate.vault || 'procedures',
              classification: useForm.classification || useTemplate.classification || 'internal',
              description: useTemplate.description || '',
            },
          },
        },
      });
      setUseTemplate(null);
    } catch (err) {
      showSnack(`Failed to open editor: ${err.message}`, 'error');
    } finally {
      setOpeningEditor(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Document Templates</Typography>
          <Typography variant="body2" color="text.secondary">
            {DEFAULT_TEMPLATES.length} professional templates ready to use • Click "Use Template" to customize and create
          </Typography>
        </Box>
        <TextField
          size="small"
          placeholder="Search templates..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
      </Box>

      {/* Category Tabs */}
      <Tabs
        value={categoryTab}
        onChange={(_, v) => setCategoryTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        {TEMPLATE_CATEGORIES.map((cat) => (
          <Tab
            key={cat}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {cat}
                {cat !== 'All' && (
                  <Badge badgeContent={DEFAULT_TEMPLATES.filter(t => t.category === cat).length} color="primary" />
                )}
              </Box>
            }
          />
        ))}
      </Tabs>

      {/* Template Grid */}
      {filteredTemplates.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <ArticleIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.3 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>No templates found</Typography>
          <Typography color="text.secondary">Try a different search or category</Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filteredTemplates.map(template => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={template.id}>
              <Card sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.2s',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 8 },
              }}>
                <CardContent sx={{ flex: 1 }}>
                  {/* Icon + Category */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{
                      width: 48, height: 48, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '1.6rem', bgcolor: `${CATEGORY_COLORS[template.category] || '#1565c0'}18`,
                      border: `1px solid ${CATEGORY_COLORS[template.category] || '#1565c0'}40`,
                    }}>
                      {template.icon}
                    </Box>
                    <Chip
                      label={template.category}
                      size="small"
                      sx={{ bgcolor: `${CATEGORY_COLORS[template.category] || '#1565c0'}18`, color: CATEGORY_COLORS[template.category] || '#1565c0', fontWeight: 700, fontSize: '0.65rem' }}
                    />
                  </Box>

                  {/* Title */}
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>{template.name}</Typography>

                  {/* Description */}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {template.description}
                  </Typography>

                  {/* Badges */}
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <ClassificationBadge classification={template.classification} />
                    <Chip label={template.vault} size="small" variant="outlined" sx={{ fontSize: '0.65rem', textTransform: 'capitalize' }} />
                  </Box>
                </CardContent>

                <CardActions sx={{ px: 2, pb: 2, gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<OpenInFullIcon />}
                    onClick={() => setPreviewTemplate(template)}
                    sx={{ flex: 1 }}
                  >
                    Preview
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<CreateNewFolderIcon />}
                    onClick={() => handleOpenUse(template)}
                    sx={{ flex: 1 }}
                  >
                    Use
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!previewTemplate} onClose={() => setPreviewTemplate(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{previewTemplate?.icon}</span>
            <Typography fontWeight={700}>{previewTemplate?.name}</Typography>
            {previewTemplate && <ClassificationBadge classification={previewTemplate.classification} />}
          </Box>
          <IconButton onClick={() => setPreviewTemplate(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '70vh', overflow: 'auto' }}>
          {previewTemplate && renderPreviewContent(previewTemplate.content)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewTemplate(null)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => { setPreviewTemplate(null); handleOpenUse(previewTemplate); }}
          >
            Customize in Editor
          </Button>
        </DialogActions>
      </Dialog>

      {/* Use Template Dialog */}
      <Dialog open={!!useTemplate} onClose={() => setUseTemplate(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography fontWeight={700}>Create from: {useTemplate?.name}</Typography>
          <IconButton onClick={() => setUseTemplate(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField label="Document Title" fullWidth value={useForm.title} onChange={e => setUseForm(p => ({ ...p, title: e.target.value }))} required />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Vault</InputLabel>
              <Select value={useForm.vault} onChange={e => setUseForm(p => ({ ...p, vault: e.target.value }))} label="Vault">
                {VAULT_OPTIONS.map(v => <MenuItem key={v} value={v} sx={{ textTransform: 'capitalize' }}>{v}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Classification</InputLabel>
              <Select value={useForm.classification} onChange={e => setUseForm(p => ({ ...p, classification: e.target.value }))} label="Classification">
                {CLASSIFICATION_OPTIONS.map(c => <MenuItem key={c} value={c} sx={{ textTransform: 'capitalize' }}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          {/* Merge fields */}
          {Object.keys(useForm.mergeFields).length > 0 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Template Fields</Typography>
              <Grid container spacing={1.5}>
                {Object.keys(useForm.mergeFields).map(field => (
                  <Grid item xs={12} sm={6} key={field}>
                    <TextField
                      size="small"
                      fullWidth
                      label={field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      placeholder={`Enter ${field}`}
                      value={useForm.mergeFields[field]}
                      onChange={e => setUseForm(p => ({ ...p, mergeFields: { ...p.mergeFields, [field]: e.target.value } }))}
                    />
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}

          <Typography variant="body2" color="text.secondary">
            Fill in the template fields, then open the Document Editor to customize chapters, sections, and images.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUseTemplate(null)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleOpenInEditor}
            disabled={openingEditor}
          >
            {openingEditor ? 'Opening…' : 'Open in Document Editor'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
